//
//  usePinnedQaProgramChecklist.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module provides use Pinned QA Program Checklist client behaviour.
//

import { useLocalStorage } from "@mantine/hooks";
import { useCallback } from "react";

import useCurrentUser from "src/hooks/authentication/useCurrentUser";

/** Identifies the one trial checklist pinned by a user in this browser. */
export interface PinnedQaProgramChecklist {
    trialId: number;
    trialName: string;
    collapsed: boolean;
}

/** Builds the browser-storage key that isolates pin state by user. */
export const getPinnedQaProgramChecklistStorageKey = (username?: string) =>
    `tmd-qa-program-checklist-pin:${(username || "anonymous").trim().toLocaleLowerCase()}`;

/** Provides persistent pin, collapse and clear actions for the current user. */
const usePinnedQaProgramChecklist = () => {
    const user = useCurrentUser();
    const [pin, setPin] = useLocalStorage<PinnedQaProgramChecklist | null>({
        key: getPinnedQaProgramChecklistStorageKey(user?.username),
        defaultValue: null,
        getInitialValueInEffect: false
    });

    const clearPin = useCallback(() => setPin(null), [setPin]);
    const toggleCollapsed = useCallback(
        () => setPin(current => (current ? { ...current, collapsed: !current.collapsed } : null)),
        [setPin]
    );

    return {
        pin,
        setPin,
        clearPin,
        toggleCollapsed
    };
};

export default usePinnedQaProgramChecklist;
